import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useCommonMap from "../../../hooks/useCommonMap";
import {
  MarkerClusterer,
  SuperClusterAlgorithm,
} from "@googlemaps/markerclusterer";
import { Circle, Polygon } from "@vis.gl/react-google-maps";
import { imageSet } from "../../../pages/icons/AssetIcon/IconList";
import { getUrl } from "../../../utils/staticMethods";
import defaultImg from "../../../assets/img/icon/Aircraft/Airliner_Black.png";
import circle from "../../../assets/img/m1.png/clusterZoneIcon.png";
import useSrlHelper from "../../../hooks/useSrlHelper";
import { createPortal } from "react-dom";
import { Badge } from "react-bootstrap";

/**
 * Displays an array of zones
 * @param {Object[]} zones // Array of nearby zones
 * @returns {React.ComponentElement}
 */
export function ZoneLayer({ zones }) {
  const {
    map,
    // /**@type {import("@vis.gl/react-google-maps").CoreLibrary} */ mapLibCore,
    // /**@type {import("@vis.gl/react-google-maps").MapsLibrary} */ mapLibMaps,
    /**@type {import("@vis.gl/react-google-maps").MarkerLibrary} */ mapLibMarker,
  } = useCommonMap();
  /**@type {import("react").MutableRefObject<MarkerClusterer>} */
  const clustererRef = useRef(null);
  const zoneMarkerMapRef = useRef(new Map());
  const isClusteringRef = useRef(false);
  const { itemDisplayName } = useSrlHelper();
  const [zonesToCluster, setZonesToCluster] = useState([]);
  const [clustererReady, setClustererReady] = useState(false);

  const renderer = useMemo(() => {
    if (!mapLibMarker) return null;
    return {
      render({ count, position, markers }, stats, map) {
        const clusterId = markers.map((m) => m.__id).join("-");
        const element = document.createElement("div");
        const marker = new mapLibMarker.AdvancedMarkerElement({
          map,
          position,
        });
        marker.__count = count;
        marker.__zone = markers[0].__zone;
        marker.__id = clusterId;
        marker.__element = element;
        marker.append(element);
        return marker;
      },
    };
  }, [mapLibMarker]);

  /**@type {import("@googlemaps/markerclusterer").onClusterClickHandler} */
  const handleClusterClick = useCallback((event, cluster, map) => {
    /**@type {google.maps.LatLngBounds} */
    const bounds = new window.google.maps.LatLngBounds();
    cluster.markers.forEach((m) => bounds.extend(m.position));
    map.fitBounds(bounds, 128);
  }, []);

  // Add name and image to zones
  useEffect(() => {
    setZonesToCluster(
      zones.map((zone) => ({
        ...zone,
        zoneName: itemDisplayName(zone),
        imageSrc: imageSet[getUrl(zone.icon)]
          ? require("../../../assets/img/icon/" + imageSet[getUrl(zone.icon)])
              .default
          : defaultImg,
      }))
    );
  }, [zones]);

  useEffect(() => {
    if (!clustererReady || !clustererRef.current || !zonesToCluster) return;

    const removeIds = new Set();
    const newIds = zonesToCluster.map((v) => {
      return v.id;
    });

    for (const existingKey of zoneMarkerMapRef.current.keys()) {
      if (!newIds.includes(existingKey)) {
        removeIds.add(existingKey);
      }
    }
    removeIds.forEach((value, index) => {
      let marker = zoneMarkerMapRef.current.get(value);
      if (marker) {
        clustererRef.current.removeMarker(marker, true);
        zoneMarkerMapRef.current.delete(value);
      }
    });

    zonesToCluster.forEach((value, index) => {
      const zone = value;
      const zoneId = zone.id;

      let marker = zoneMarkerMapRef.current.get(zoneId);
      if (!marker) {
        const element = document.createElement("div");
        marker = new mapLibMarker.AdvancedMarkerElement({
          map,
          position: { lat: Number(zone.latitude), lng: Number(zone.longitude) },
        });
        marker.append(element);
        marker.__element = element;
        marker.__count = 1;
        marker.__id = zoneId;

        clustererRef.current.addMarker(marker, true);
        zoneMarkerMapRef.current.set(zoneId, marker);
      }
      marker.__zone = zone;
    });
    clustererRef.current.render();
  }, [clustererReady, zonesToCluster]);

  useEffect(() => {
    if (!map || !mapLibMarker) {
      setClustererReady(false);
      return;
    }

    console.log("create clusterer");

    const clusterer = new MarkerClusterer({
      map,
      algorithm: new SuperClusterAlgorithm({
        radius: 100,
        maxZoom: 15,
      }),
      renderer,
      onClusterClick: handleClusterClick,
    });

    // --- clusteringbegin ---
    clusterer.addListener("clusteringbegin", (c) => {
      isClusteringRef.current = true;
    });

    // --- clusteringend ---
    clusterer.addListener("clusteringend", (c) => {
      isClusteringRef.current = false;
    });

    clustererRef.current = clusterer;
    setClustererReady(true);

    return () => {
      clusterer.setMap(null);
      clustererRef.current = null;
      setClustererReady(false);
    };
  }, [map, mapLibMarker]);

  return (
    <>
      {clustererRef.current &&
        clustererRef.current.clusters &&
        clustererRef.current.clusters.map((cluster) => {
          const count = cluster.count;
          if (count === 1) {
            // Single zone
            const id = cluster.markers[0].__id;
            const element = cluster.markers[0].__element;
            const zone = cluster.markers[0].__zone;
            return createPortal(
              <>
                <div
                  style={{ position: "relative", transform: "translateY(50%)" }}
                >
                  <img src={zone.imageSrc} width={24} alt="Marker" />
                  <div
                    className="px-1 text-nowrap text-black bg-secondary bg-opacity-75 rounded"
                    style={{
                      position: "absolute",
                      bottom: "100%",
                      left: "50%",
                      transform: "translateX(-50%)",
                      marginTop: 4,
                      pointerEvents: "none",
                    }}
                  >
                    {zone.zoneName}
                  </div>
                  {zone.polygon && zone.polygon.length > 0 && (
                    <Polygon
                      key={zone.id}
                      paths={zone.polygon.map((item) => {
                        let me = item.split(",");
                        let lng = Number(me[0]);
                        let lat = Number(me[1]);
                        return { lng, lat };
                      })}
                      options={{
                        strokeColor: "#000000",
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                        fillColor: "#242424",
                        fillOpacity: 0.4,
                      }}
                    />
                  )}
                  {!zone.polygon && (
                    <Circle
                      center={{
                        lng: Number(zone.longitude),
                        lat: Number(zone.latitude),
                      }}
                      radius={Number(zone.radius)}
                      options={{
                        strokeColor: "#000000",
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                        fillColor: "#242424",
                        fillOpacity: 0.4,
                      }}
                    />
                  )}
                </div>
              </>,
              element,
              id
            );
          } else {
            // Clustered zones
            const id = cluster.marker.__id;
            const element = cluster.marker.__element;
            return createPortal(
              <>
                <div
                  style={{ position: "relative", transform: "translateY(50%)" }}
                >
                  <img src={circle} width={"24px"} alt="Cluster" />
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: "50%",
                      transform: "translateX(-50%)",
                      marginTop: 4,
                      pointerEvents: "none",
                    }}
                  >
                    <Badge pill bg="secondary">
                      {count}
                    </Badge>
                  </div>
                </div>
              </>,
              element,
              id
            );
          }
        })}
    </>
  );
}
